import { useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { isIOSSelector } from '../../../../../shared/features/uatraits/selectors';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { playMediaElement } from '../../../../../shared/helpers/play-media-element';
import { selectIsFullscreen, setIsFullscreenAction } from '../slice';

const logger = createLogger('fullscreen');

const getPresentationMode = (videoElement?: HTMLVideoElement | null) => {
    return (videoElement as HTMLVideoElement & { webkitPresentationMode?: string })?.webkitPresentationMode;
};

const getPlaybackAdditional = (videoElement: HTMLVideoElement | null | undefined, eventName: string) => {
    return {
        currentTime: videoElement?.currentTime,
        eventName,
        paused: videoElement?.paused,
        readyState: videoElement?.readyState,
        surface: 'call-fullscreen',
        webkitPresentationMode: getPresentationMode(videoElement),
    };
};

export const useToggleFullscreen = (videoElementRef?: HTMLVideoElement | null) => {
    const dispatch = useDispatch();
    const isIOS = useSelector(isIOSSelector);
    const isFullscreen = useSelector(selectIsFullscreen);

    const toggleFullscreen = useCallback(() => {
        if (videoElementRef && isIOS) {
            /** Без контролов фул-скрин не включается, пользователь не успевает их увидеть */
            videoElementRef.controls = true;
            /** При выходе из fullscreen видео встаёт на паузу.
             *  Приходится переключать автовоспроизведение, чтобы работал HTMLVideoElement.play()
             *  Иначе возникает ошибка Invalid State Error, мол, нет жеста пользователя */
            videoElementRef.autoplay = false;
            videoElementRef.volume = 0;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error - ios specific property
            videoElementRef.webkitEnterFullscreen();
        } else {
            dispatch(setIsFullscreenAction(!isFullscreen));
        }
    }, [videoElementRef, isIOS, dispatch, isFullscreen]);

    useEffect(() => {
        if (isIOS) {
            const currentVideoRef = videoElementRef;
            const onEndFullscreen = (event: Event) => {
                if (currentVideoRef) {
                    /** На выходе из fullscreen выключаем контролы и включаем автовоспроизведение */
                    currentVideoRef.controls = false;
                    currentVideoRef.autoplay = true;
                    playMediaElement(currentVideoRef, (error) => {
                        logger.warn(error, {
                            method: 'resumePlaybackAfterFullscreen',
                            additional: getPlaybackAdditional(currentVideoRef, event.type),
                        });
                    });
                }
            };
            const onPause = (event: Event) => {
                const target = event.target as HTMLVideoElement;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error - webkitPresentationMode is ios specific property
                if (target?.play && typeof target?.play === 'function' && target?.webkitPresentationMode === 'inline') {
                    /** Видео встаёт на паузу на выходе из fullscreen, продолжаем воспроизводить */
                    playMediaElement(target, (error) => {
                        logger.warn(error, {
                            method: 'resumePlaybackOnPause',
                            additional: getPlaybackAdditional(target, 'pause'),
                        });
                    });
                }
            };

            const onEmptied = (event: Event) => {
                const target = event.target as HTMLVideoElement;
                /* eslint-disable @typescript-eslint/ban-ts-comment */
                // @ts-expect-error - webkitPresentationMode is ios specific property
                if (isIOS && target && target.webkitPresentationMode === 'fullscreen') {
                    // @ts-expect-error - webkitExitFullscreen is ios specific property
                    target.webkitExitFullscreen();
                }
                /* eslint-enable @typescript-eslint/ban-ts-comment */
            };

            if (currentVideoRef) {
                currentVideoRef.addEventListener('webkitendfullscreen', onEndFullscreen);
                currentVideoRef.addEventListener('leavepictureinpicture', onEndFullscreen);
                currentVideoRef.addEventListener('emptied', onEmptied);
                currentVideoRef.addEventListener('pause', onPause);
            }
            return () => {
                if (currentVideoRef) {
                    currentVideoRef.removeEventListener('webkitendfullscreen', onEndFullscreen);
                    currentVideoRef.removeEventListener('leavepictureinpicture', onEndFullscreen);
                    currentVideoRef.removeEventListener('emptied', onEmptied);
                    currentVideoRef.removeEventListener('pause', onPause);
                }
            };
        }
    }, [videoElementRef, toggleFullscreen, isIOS]);

    return {
        toggleFullscreen,
    };
};
